import { isDefined } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import type {
  GroupTotals,
  ServiceUsagesGroup,
} from '../hooks/useFullyConsolidatedServiceUsages.hook';

type Amount = GroupTotals['discountedAmountExcludingVat'];

export interface FeesLine {
  readonly _id: Id;
  readonly group: Pick<ServiceUsagesGroup, 'label' | 'sectionTitle'>;
  readonly totals: GroupTotals | null;
}

export interface FeesModel {
  readonly mandatoryLines: readonly FeesLine[];
  readonly mandatoryTotal: GroupTotals | null;
  // Each option, one by one, without total.
  readonly optionLines: readonly FeesLine[];
}

const UNNAMED_GROUP_LABEL = 'Prestations';

// The groups of a collaboration come from its sub-missions, named by their
// title.
export const feesLineLabelOf = ({
  sectionTitle,
  label,
}: FeesLine['group']): string =>
  [sectionTitle, label].filter(Boolean).join(' - ') || UNNAMED_GROUP_LABEL;

const toLine = (group: Readonly<ServiceUsagesGroup>): FeesLine => ({
  _id: group._id,
  group,
  totals: group.totals,
});

const sumOf = (
  [first, ...others]: readonly [GroupTotals, ...GroupTotals[]],
  amountOf: (groupTotals: GroupTotals) => Amount,
): Amount => ({
  currencyId: amountOf(first).currencyId,
  value: [first, ...others].reduce(
    (sum, groupTotals) => sum + amountOf(groupTotals).value,
    0,
  ),
});

const percentOf = (part: Amount, whole: Amount): number =>
  whole.value ? (part.value / whole.value) * 100 : 0;

export const sumGroupTotals = (
  groupsTotals: readonly GroupTotals[],
): GroupTotals | null => {
  const [first, ...others] = groupsTotals;

  if (!first) {
    return null;
  }

  const totals = [first, ...others] as const;

  const undiscountedAmountExcludingVat = sumOf(
    totals,
    (groupTotals) => groupTotals.undiscountedAmountExcludingVat,
  );
  const discountAsAmount = sumOf(
    totals,
    (groupTotals) => groupTotals.discountAsAmount,
  );
  const discountedAmountExcludingVat = sumOf(
    totals,
    (groupTotals) => groupTotals.discountedAmountExcludingVat,
  );
  const vatAsAmount = sumOf(totals, (groupTotals) => groupTotals.vatAsAmount);

  return {
    undiscountedAmountExcludingVat,
    discountAsAmount,
    discountAsPercentage: percentOf(
      discountAsAmount,
      undiscountedAmountExcludingVat,
    ),
    discountedAmountExcludingVat,
    discountedAmountIncludingVat: sumOf(
      totals,
      (groupTotals) => groupTotals.discountedAmountIncludingVat,
    ),
    vatAsAmount,
    vatAsPercentage: percentOf(vatAsAmount, discountedAmountExcludingVat),
  };
};

// The groups at 0 (the own group of a collaboration left empty) are left out.
const isPriced = ({ totals }: Readonly<ServiceUsagesGroup>): boolean =>
  !!totals && totals.undiscountedAmountExcludingVat.value !== 0;

export const buildFeesModel = (
  groups: readonly ServiceUsagesGroup[],
): FeesModel => {
  const mandatoryGroups = groups.filter((group) => !group.isOptional);

  return {
    mandatoryLines: mandatoryGroups.filter(isPriced).map(toLine),
    mandatoryTotal: sumGroupTotals(
      mandatoryGroups.map((group) => group.totals).filter(isDefined),
    ),
    optionLines: groups
      .filter((group) => group.isOptional && isPriced(group))
      .map(toLine),
  };
};
